import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Send } from "lucide-react";
import { countSegment, getSegment, listSegments, saveSegment, segmentMembers } from "@/lib/emailApi";
import { filterToSegment } from "@/lib/segments";
import { BRANDS } from "@/lib/brands";
import { formatInt } from "@/lib/format";
import type { SegmentDefinition } from "@/lib/types";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useSavedSearches } from "../contacts/ContactsListPage";
import { RuleList } from "./RuleEditor";

/** Build a segment: rules on the left, a live count (and who's left out, and why) on the right. */
export function SegmentEditorPage() {
  const { segmentId } = useParams({ from: "/email/segments/$segmentId" });
  const { from_search } = useSearch({ from: "/email/segments/$segmentId" });
  const isNew = segmentId === "new";
  const id = isNew ? 0 : Number(segmentId);
  const existing = useQuery({ queryKey: ["segment", id], queryFn: () => getSegment(id), enabled: id > 0 });
  const saved = useSavedSearches();
  const others = useQuery({ queryKey: ["segments"], queryFn: () => listSegments() });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [brand, setBrand] = useState<number | "">(2);
  const [def, setDef] = useState<SegmentDefinition>({ match: "all", rules: [] });
  const [dropped, setDropped] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    if (existing.data) {
      const s = existing.data;
      setName(s.name);
      setDescription(s.description ?? "");
      setBrand(s.brand_id ?? "");
      setDef({ match: s.definition?.match ?? "all", rules: s.definition?.rules ?? [], include_skip_marketing: s.definition?.include_skip_marketing });
      setLoaded(true);
    } else if (isNew && from_search && saved.data) {
      const ss = saved.data.saved_searches.find((x) => x.id === from_search);
      if (ss) {
        const conv = filterToSegment(ss.filters);
        setName(ss.name);
        setDescription(ss.description ?? `From the saved search “${ss.name}”`);
        if (ss.filters.brand_id) setBrand(ss.filters.brand_id);
        setDef(conv.definition);
        setDropped(conv.dropped);
        setLoaded(true);
      }
    } else if (isNew && !from_search) {
      setLoaded(true);
    }
  }, [existing.data, saved.data, isNew, from_search, loaded]);

  // Live count, debounced.
  const [debounced, setDebounced] = useState(def);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(def), 500);
    return () => clearTimeout(t);
  }, [def]);
  const count = useQuery({
    queryKey: ["segment-count", JSON.stringify(debounced), brand],
    queryFn: () => countSegment({ definition: debounced, brand_id: brand || undefined }),
    enabled: loaded,
    placeholderData: (p) => p,
    retry: false,
  });
  const members = useQuery({
    queryKey: ["segment-members", JSON.stringify(debounced), brand],
    queryFn: () => segmentMembers({ definition: debounced, brand_id: brand || undefined, limit: 8 }),
    enabled: loaded,
    placeholderData: (p) => p,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => saveSegment({ id: id || undefined, name: name.trim(), brand_id: brand || undefined, description, definition: def }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["segments"] });
      toast(`Saved segment “${r.name}”`);
      if (isNew) navigate({ to: "/email/segments/$segmentId", params: { segmentId: String(r.id) }, replace: true });
    },
    onError: (e) => toast(e.message, "error"),
  });

  if (!loaded && existing.error) return <p className="text-sm text-[var(--color-destructive)]">Couldn't load this segment: {existing.error.message}</p>;
  if (!loaded && (existing.isLoading || (from_search && saved.isLoading))) return <Skeleton className="h-96" />;
  const c = count.data;
  const segOptions = (others.data ?? []).filter((s) => s.id !== id).map((s) => ({ id: s.id, name: s.name }));

  return (
    <div className="mx-auto max-w-[1300px] space-y-3">
      <Link to="/email/segments" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Segments
      </Link>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1 space-y-1">
          <Label htmlFor="seg-name">Segment name</Label>
          <Input id="seg-name" className="h-10 text-lg font-semibold" value={name} onChange={(e) => setName(e.target.value)} placeholder="MHU newsletter, opened in the last 90 days" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="seg-brand">Usually mailed for</Label>
          <Select id="seg-brand" className="w-56" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")}>
            <option value="">No particular brand</option>
            {BRANDS.map((b) => (
              <option key={b.id} value={b.id}>
                {b.short}: {b.name}
              </option>
            ))}
          </Select>
        </div>
        <Button disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
          Save segment
        </Button>
        {id > 0 && (
          <Button variant="outline" asChild>
            <Link to="/email/broadcasts/$broadcastId" params={{ broadcastId: "new" }} search={{ segment: id, brand: brand || undefined }}>
              <Send /> New broadcast to it
            </Link>
          </Button>
        )}
      </div>
      <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What it's for (optional)" aria-label="Description" />
      {dropped.length > 0 && (
        <p className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-sm text-[var(--color-warning-fg)]">
          Made from a saved search. These parts of it can't be part of an email audience and were left out: {dropped.join(", ")}.
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              People who match
              <Select className="w-auto" value={def.match ?? "all"} onChange={(e) => setDef({ ...def, match: e.target.value as "all" | "any" })} aria-label="Match all or any">
                <option value="all">all</option>
                <option value="any">any</option>
              </Select>
              of these rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {def.rules.length === 0 && <p className="text-sm text-[var(--color-muted-foreground)]">No rules yet: that's everyone who can get email. Add a rule to narrow it down.</p>}
            <RuleList rules={def.rules} onChange={(rules) => setDef({ ...def, rules })} segments={segOptions} />
            <label className="flex items-start gap-2 border-t pt-3 text-sm">
              <Checkbox className="mt-0.5" checked={!!def.include_skip_marketing} onChange={(e) => setDef({ ...def, include_skip_marketing: e.target.checked || undefined })} />
              <span>
                Include people with “no marketing” tags (Opt-Out 923, KLEAN13 BAD LIST, Email Typos)
                <span className="block text-xs text-[var(--color-muted-foreground)]">Off by default. Turn on only for the win-back email or something they asked for.</span>
              </span>
            </label>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>Who's in it</CardTitle>
              {count.isFetching && <span className="text-xs text-[var(--color-muted-foreground)]">counting…</span>}
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {count.error && <p className="text-[var(--color-destructive)]">{count.error.message}</p>}
              {c && (
                <>
                  <div>
                    <div className="text-3xl font-semibold leading-none">{formatInt(c.mailable)}</div>
                    <div className="text-xs text-[var(--color-muted-foreground)]">would get the email{brand ? ` from ${BRANDS.find((b) => b.id === brand)?.short}` : ""}</div>
                  </div>
                  <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 border-t pt-2">
                    <dt className="text-[var(--color-muted-foreground)]">Match the rules</dt>
                    <dd className="num text-right">{formatInt(c.matching)}</dd>
                    <dt className="text-[var(--color-muted-foreground)]">No address that can get mail</dt>
                    <dd className="num text-right">{formatInt(c.no_mailable_address)}</dd>
                    <dt className="text-[var(--color-muted-foreground)]">Unsubscribed from this brand</dt>
                    <dd className="num text-right">{formatInt(c.unsubscribed_brand)}</dd>
                    <dt className="text-[var(--color-muted-foreground)]">Left out by “no marketing” tags</dt>
                    <dd className="num text-right">{formatInt(c.skip_marketing_tags)}</dd>
                  </dl>
                  <p className="text-xs text-[var(--color-muted-foreground)]">Counted in {c.ms} ms.</p>
                </>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>A few of them</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              {members.error && <p className="text-[var(--color-destructive)]">Couldn't list them: {members.error.message}</p>}
              {!members.error && (members.data ?? []).length === 0 && <p className="text-[var(--color-muted-foreground)]">Nobody yet.</p>}
              {(members.data ?? []).map((m) => (
                <div key={m.contact_id} className="flex items-center justify-between gap-2">
                  <Link to="/contacts/$contactId" params={{ contactId: String(m.contact_id) }} className="truncate text-[var(--color-primary)] hover:underline">
                    {[m.first_name, m.last_name].filter(Boolean).join(" ") || `#${m.contact_id}`}
                  </Link>
                  <span className={m.mailable ? "truncate text-xs text-[var(--color-muted-foreground)]" : "text-xs text-[var(--color-warning-fg)]"}>{m.mailable ? m.email : "won't get email"}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
